import { Tooltip } from "@/base";
import type {
    AIActionContext,
    AIActionResponse,
    AIContext,
} from "@/hooks/use-api-service/ai-api-service";
import {
    Button,
    ButtonGroup,
    cn,
    Modal,
    ModalBody,
    ModalContent,
    ModalFooter,
    ModalHeader,
    Textarea,
    useDisclosure,
} from "@heroui/react";
import type { Key } from "@react-types/shared";
import {
    IconAlertTriangle,
    IconBulb,
    IconCircleCheck,
    IconRestore,
    IconWand,
} from "@tabler/icons-react";
import {
    type ReactNode,
    useCallback,
    useEffect,
    useMemo,
    useState,
} from "react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { HintFields } from "../hint";
import { FormTextArrayField, type TextArrayValue } from "../text-array-field";

export interface ConversationCache<I extends string | TextArrayValue[]> {
    threadId: string;
    lastCreatedResults: I[];
}

export interface MagicWandProps<
    C extends AIContext,
    I extends string | TextArrayValue[],
> {
    // has to include user company id/job position id etc. - like the actual unique context
    id: string; // combination out of form id and fieldName (should not change on reload/rebuild)
    showPopover?: boolean;
    additionalInformationEnabled?: boolean;
    additionalInformationRequired?: boolean;
    additionalInformationLabel?: string;
    additionalInformationHint?: ReactNode;
    rotateResultsAfter?: number;
    context: C;
    onCreate: (params: {
        context: AIActionContext<C>;
        additionalInformation?: string;
    }) => Promise<AIActionResponse>;
    onUpdate?: (params: {
        context: AIActionContext<C>;
        currentValue: string;
        threadId: string;
        prompt: string;
    }) => Promise<AIActionResponse>;
    onResult: (value: I) => void;
    inputValue: I;
    className?: string;
}

const minAIInfoLength = 10;

export const MagicWand = <
    C extends AIContext,
    I extends string | TextArrayValue[],
>({
    id,
    showPopover = true,
    rotateResultsAfter,
    context,
    onCreate,
    onUpdate,
    onResult,
    inputValue,
    className,
    additionalInformationEnabled = false,
    additionalInformationRequired = false,
    additionalInformationLabel,
    additionalInformationHint,
}: MagicWandProps<C, I>) => {
    const [loading, setLoading] = useState(false);
    const [value, setValue] = useState("");
    const { isOpen, onOpen, onOpenChange, onClose } = useDisclosure();

    const formController = useForm({
        defaultValues: {
            previousPreviewArray: null,
            previousPreviewText: null,
            previewArray: Array.isArray(inputValue) ? inputValue : [],
            previewText: typeof inputValue === "string" ? inputValue : "",
        },
    });

    const watchedPreviousPreviewArray = formController.watch(
        "previousPreviewArray",
    );
    const watchedPreviousPreviewText = formController.watch(
        "previousPreviewText",
    );
    const watchedPreviewArray = formController.watch("previewArray");
    const watchedPreviewText = formController.watch("previewText");

    const cacheId = `aiCache_${id}`;

    // get old values for this field
    const [conversationCache, setConversationCache] = useState<
        ConversationCache<I>
    >(JSON.parse(localStorage.getItem(cacheId)));

    const [tab, setTab] = useState<Key>(
        inputValue?.length && onUpdate ? "update" : "create",
    );

    const isUpdateable = !!onUpdate;

    // biome-ignore lint/correctness/useExhaustiveDependencies: inputValue needs to be stringified because it can be an array
    useEffect(() => {
        formController.setValue(
            "previewArray",
            Array.isArray(inputValue) ? inputValue : [],
        );
        formController.setValue(
            "previewText",
            typeof inputValue === "string" ? inputValue : "",
        );
        setTab(inputValue?.length && isUpdateable ? "update" : "create");
    }, [JSON.stringify(inputValue), isUpdateable]);

    const handleSubmit = async () => {
        if (
            conversationCache?.lastCreatedResults?.length >= rotateResultsAfter
        ) {
            const currentIndex = conversationCache.lastCreatedResults.findIndex(
                (val) => JSON.stringify(val) === JSON.stringify(inputValue),
            );

            // check if value was found in cache
            if (currentIndex >= 0) {
                let newValue: I;

                // check if already last item
                if (
                    currentIndex >=
                    conversationCache.lastCreatedResults.length - 1
                ) {
                    newValue = conversationCache.lastCreatedResults[0];
                } else {
                    newValue =
                        conversationCache.lastCreatedResults[currentIndex + 1];
                }
                onResult(newValue);
                onClose();
                return;
            }
        }

        if (watchedPreviewArray?.length) {
            formController.setValue(
                "previousPreviewArray",
                watchedPreviewArray,
            );
        } else if (watchedPreviewText) {
            formController.setValue("previousPreviewText", watchedPreviewText);
        }
        setLoading(true);

        const actionContext = Object.fromEntries(
            Object.entries(context).map(([k, v]) => [k, v.value]),
        ) as AIActionContext<C>;

        try {
            const result =
                tab === "create"
                    ? await onCreate({
                          context: actionContext,
                          additionalInformation: value,
                      })
                    : await onUpdate({
                          context: actionContext,
                          currentValue: Array.isArray(inputValue)
                              ? watchedPreviewArray
                                    .map((v) => v.content)
                                    .join("/n")
                              : watchedPreviewText,
                          threadId: conversationCache?.threadId,
                          prompt: value,
                      });

            // decide if response should be converted into an array for creating lists
            const formattedValue = (
                Array.isArray(inputValue)
                    ? (result.value.split("\n").map((v) => ({
                          content: v,
                      })) satisfies TextArrayValue[])
                    : (result.value satisfies string)
            ) as I;
            // set result of endpoint as form input value and update conversationCache with message and result
            if (Array.isArray(formattedValue)) {
                formController.setValue("previewArray", formattedValue);
            } else if (typeof formattedValue === "string") {
                formController.setValue("previewText", formattedValue);
            }
            const newCache = {
                threadId: result.threadId,
                lastCreatedResults:
                    tab === "create"
                        ? [
                              ...(conversationCache?.lastCreatedResults ?? []),
                              formattedValue,
                          ]
                        : (conversationCache?.lastCreatedResults ?? []),
            };
            setConversationCache(newCache);
            if (!cacheId.includes("undefined")) {
                localStorage.setItem(cacheId, JSON.stringify(newCache));
            }
        } catch (e) {
            console.error(e);
            toast.error("KI-Generierung fehlgeschlagen");
        }
        setLoading(false);
    };

    const handleApply = useCallback(() => {
        // set result of endpoint as form input value
        onResult(
            Array.isArray(inputValue)
                ? (watchedPreviewArray as I)
                : (watchedPreviewText as I),
        );
        onClose();
    }, [
        onClose,
        onResult,
        inputValue,
        watchedPreviewArray,
        watchedPreviewText,
    ]);

    const handleAbort = useCallback(() => {
        onClose();
        setValue("");
        formController.setValue(
            "previewArray",
            Array.isArray(inputValue) ? inputValue : [],
        );
        formController.setValue(
            "previewText",
            typeof inputValue === "string" ? inputValue : "",
        );
    }, [onClose, inputValue, formController.setValue]);

    const contextItemsWithAdditionalInfo = useMemo(
        () => [
            ...Object.values(context),
            ...(additionalInformationEnabled || tab === "update"
                ? [
                      {
                          required:
                              additionalInformationRequired || tab === "update",
                          label: "Informationen für KI",
                          value:
                              value && value.length >= minAIInfoLength
                                  ? value
                                  : undefined, // force value to be falsy, if too short
                      },
                  ]
                : []),
        ],
        [
            context,
            additionalInformationEnabled,
            additionalInformationRequired,
            tab,
            value,
        ],
    );

    const getMissingContext = (required = false) =>
        contextItemsWithAdditionalInfo.filter((item) =>
            required ? item.required && !item.value : !item.value,
        );

    return (
        <>
            <Button
                type="button"
                isLoading={loading}
                isDisabled={loading}
                onPress={showPopover ? onOpen : handleSubmit}
                isIconOnly={true}
                size="sm"
                variant="flat"
                color="secondary"
                className={cn("cursor-pointer text-secondary", className)}
            >
                <IconWand size={16} stroke={1.5} />
            </Button>
            <Modal
                size="5xl"
                classNames={{
                    base: "max-w-7xl border-secondary border-t-5",
                    body: "pb-0",
                }}
                isOpen={isOpen}
                onOpenChange={onOpenChange}
                isDismissable={false}
                hideCloseButton={
                    !!value.length ||
                    !!watchedPreviewArray?.length ||
                    !!watchedPreviewText?.length
                }
                disableAnimation
            >
                <ModalContent>
                    <ModalHeader>KI Unterstützung</ModalHeader>
                    <ModalBody className="flex flex-row gap-6">
                        <div className="flex-1">
                            <ButtonGroup>
                                <Button
                                    variant="flat"
                                    color={
                                        tab === "create"
                                            ? "secondary"
                                            : "default"
                                    }
                                    onPress={() => {
                                        setTab("create");
                                        setValue("");
                                    }}
                                >
                                    {additionalInformationEnabled
                                        ? "Ausformulieren"
                                        : "Generieren"}
                                </Button>
                                <Button
                                    variant="flat"
                                    color={
                                        tab === "update"
                                            ? "secondary"
                                            : "default"
                                    }
                                    onPress={() => {
                                        setTab("update");
                                        setValue("");
                                    }}
                                    isDisabled={
                                        !(
                                            (watchedPreviewArray?.length ||
                                                watchedPreviewText?.length) &&
                                            onUpdate
                                        )
                                    }
                                >
                                    Verbessern
                                </Button>
                            </ButtonGroup>
                            <div className="flex flex-1 flex-col gap-7 py-4 pb-0">
                                {tab === "create" && (
                                    <>
                                        {additionalInformationEnabled && (
                                            <Textarea
                                                label={
                                                    additionalInformationLabel ||
                                                    "Zusätzliche Informationen hier optional eingeben."
                                                }
                                                endContent={
                                                    <div className="absolute top-3 right-3 flex shrink-0 items-center gap-1">
                                                        {additionalInformationHint && (
                                                            <Tooltip
                                                                color="warning"
                                                                content={
                                                                    additionalInformationHint
                                                                }
                                                            >
                                                                <Button
                                                                    variant="light"
                                                                    isIconOnly={
                                                                        true
                                                                    }
                                                                    size="sm"
                                                                >
                                                                    <IconBulb
                                                                        stroke={
                                                                            1.5
                                                                        }
                                                                        size={
                                                                            18
                                                                        }
                                                                    />
                                                                </Button>
                                                            </Tooltip>
                                                        )}
                                                    </div>
                                                }
                                                value={value}
                                                onValueChange={setValue}
                                                classNames={{ base: "mt-auto" }}
                                                minRows={7}
                                                maxRows={30}
                                                minLength={minAIInfoLength}
                                            />
                                        )}
                                        <div className="flex items-center gap-3">
                                            <Button
                                                type="button"
                                                fullWidth={true}
                                                color="secondary"
                                                size="lg"
                                                variant="bordered"
                                                startContent={
                                                    loading ? undefined : (
                                                        <IconWand
                                                            stroke={1.5}
                                                        />
                                                    )
                                                }
                                                isLoading={loading}
                                                isDisabled={
                                                    loading ||
                                                    !!getMissingContext(true)
                                                        .length
                                                }
                                                onPress={handleSubmit}
                                            >
                                                {additionalInformationEnabled
                                                    ? "Ausformulieren"
                                                    : "Generieren"}
                                            </Button>
                                            <Tooltip
                                                color="danger"
                                                content={
                                                    <HintFields
                                                        missingFieldLabels={getMissingContext().map(
                                                            (item) =>
                                                                item.label,
                                                        )}
                                                        requiredFields={contextItemsWithAdditionalInfo
                                                            .filter(
                                                                (item) =>
                                                                    item.required,
                                                            )
                                                            .map(
                                                                (item) =>
                                                                    item.label,
                                                            )}
                                                        optionalFields={contextItemsWithAdditionalInfo
                                                            .filter(
                                                                (item) =>
                                                                    !item.required,
                                                            )
                                                            .map(
                                                                (item) =>
                                                                    item.label,
                                                            )}
                                                    />
                                                }
                                            >
                                                {getMissingContext(true)
                                                    .length ? (
                                                    <IconAlertTriangle
                                                        size={28}
                                                        stroke={1.5}
                                                        className="cursor-pointer text-danger"
                                                    />
                                                ) : getMissingContext()
                                                      .length ? (
                                                    <IconAlertTriangle
                                                        size={28}
                                                        stroke={1.5}
                                                        className="cursor-pointer text-warning"
                                                    />
                                                ) : (
                                                    <IconCircleCheck
                                                        size={28}
                                                        stroke={1.5}
                                                        className="cursor-pointer text-success"
                                                    />
                                                )}
                                            </Tooltip>
                                        </div>
                                    </>
                                )}
                                {tab === "update" && (
                                    <>
                                        <Textarea
                                            label="Wie soll der Inhalt angepasst werden?"
                                            value={value}
                                            onValueChange={setValue}
                                            classNames={{ base: "mt-auto" }}
                                            minRows={7}
                                            maxRows={30}
                                            minLength={minAIInfoLength}
                                            errorMessage={(v) =>
                                                v.validationDetails.tooShort
                                                    ? `Bitte erweitern Sie diesen Text auf ${minAIInfoLength} oder mehr Zeichen (derzeit sind es ${value.length} Zeichen).`
                                                    : undefined
                                            }
                                        />
                                        <div className="flex items-center gap-3">
                                            <Button
                                                type="button"
                                                fullWidth={true}
                                                size="lg"
                                                color="secondary"
                                                variant="bordered"
                                                startContent={
                                                    loading ? undefined : (
                                                        <IconWand
                                                            stroke={1.5}
                                                        />
                                                    )
                                                }
                                                isLoading={loading}
                                                isDisabled={
                                                    loading ||
                                                    !!getMissingContext(true)
                                                        .length
                                                }
                                                onPress={handleSubmit}
                                            >
                                                Anpassen
                                            </Button>
                                            <Tooltip
                                                color="danger"
                                                content={
                                                    <HintFields
                                                        missingFieldLabels={getMissingContext().map(
                                                            (item) =>
                                                                item.label,
                                                        )}
                                                        requiredFields={contextItemsWithAdditionalInfo
                                                            .filter(
                                                                (item) =>
                                                                    item.required,
                                                            )
                                                            .map(
                                                                (item) =>
                                                                    item.label,
                                                            )}
                                                        optionalFields={contextItemsWithAdditionalInfo
                                                            .filter(
                                                                (item) =>
                                                                    !item.required,
                                                            )
                                                            .map(
                                                                (item) =>
                                                                    item.label,
                                                            )}
                                                    />
                                                }
                                            >
                                                {getMissingContext(true)
                                                    .length ? (
                                                    <IconAlertTriangle
                                                        size={28}
                                                        stroke={1.5}
                                                        className="cursor-pointer text-danger"
                                                    />
                                                ) : getMissingContext()
                                                      .length ? (
                                                    <IconAlertTriangle
                                                        size={28}
                                                        stroke={1.5}
                                                        className="cursor-pointer text-warning"
                                                    />
                                                ) : (
                                                    <IconCircleCheck
                                                        size={28}
                                                        stroke={1.5}
                                                        className="cursor-pointer text-success"
                                                    />
                                                )}
                                            </Tooltip>
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                        <div className="flex-1">
                            <h2 className="mb-7 font-medium text-lg">
                                Vorschau
                            </h2>
                            {Array.isArray(inputValue) ? (
                                <FormTextArrayField
                                    label=""
                                    fieldName="previewArray"
                                    valuePropertyName="content"
                                    hideItemLabel={true}
                                    hideAddButton={true}
                                    size="md"
                                    formController={formController}
                                    className="min-h-[180px]"
                                    cardEndContent={() => (
                                        <Tooltip content="Vorherigen Text wiederherstellen">
                                            <Button
                                                isIconOnly={true}
                                                size="sm"
                                                onPress={() => {
                                                    formController.setValue(
                                                        "previewArray",
                                                        watchedPreviousPreviewArray,
                                                    );
                                                }}
                                                isDisabled={
                                                    !watchedPreviousPreviewArray ||
                                                    JSON.stringify(
                                                        watchedPreviousPreviewArray,
                                                    ) ===
                                                        JSON.stringify(
                                                            watchedPreviewArray,
                                                        )
                                                }
                                                className="ml-auto"
                                            >
                                                <IconRestore
                                                    stroke={1.5}
                                                    size={20}
                                                />
                                            </Button>
                                        </Tooltip>
                                    )}
                                />
                            ) : (
                                <Textarea
                                    value={watchedPreviewText}
                                    onValueChange={(v) => {
                                        formController.setValue(
                                            "previewText",
                                            v,
                                        );
                                    }}
                                    minRows={12}
                                    maxRows={30}
                                    endContent={
                                        <Tooltip content="Vorherigen Text wiederherstellen">
                                            <Button
                                                isIconOnly={true}
                                                size="sm"
                                                onPress={() => {
                                                    formController.setValue(
                                                        "previewText",
                                                        watchedPreviousPreviewText,
                                                    );
                                                }}
                                                isDisabled={
                                                    !watchedPreviousPreviewText ||
                                                    watchedPreviousPreviewText ===
                                                        watchedPreviewText
                                                }
                                                className="mt-auto"
                                            >
                                                <IconRestore
                                                    stroke={1.5}
                                                    size={20}
                                                />
                                            </Button>
                                        </Tooltip>
                                    }
                                />
                            )}
                        </div>
                    </ModalBody>
                    <ModalFooter>
                        <Button variant="light" onPress={handleAbort}>
                            Verwerfen
                        </Button>
                        <Button color="secondary" onPress={handleApply}>
                            Übernehmen
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </>
    );
};
